import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

const NEW_YORK_TIME_ZONE = 'America/New_York';

export type UsEquityMarketDateStatus = {
  dateKey: string;
  valid: boolean;
  reason: string | null;
  holidayName: string | null;
  isFuture: boolean;
};

const pad = (value: number) => String(value).padStart(2, '0');

function dateKeyFromUtcDate(date: Date): string {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

function utcDateFromKey(dateKey: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateKey || '').trim());
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return dateKeyFromUtcDate(date) === dateKey ? date : null;
}

function addUtcDays(date: Date, days: number): Date {
  const next = new Date(date.getTime());
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function nthWeekdayOfMonth(year: number, month: number, weekday: number, nth: number): Date {
  const first = new Date(Date.UTC(year, month, 1));
  const offset = (weekday - first.getUTCDay() + 7) % 7;
  return new Date(Date.UTC(year, month, 1 + offset + ((nth - 1) * 7)));
}

function lastWeekdayOfMonth(year: number, month: number, weekday: number): Date {
  const last = new Date(Date.UTC(year, month + 1, 0));
  const offset = (last.getUTCDay() - weekday + 7) % 7;
  return addUtcDays(last, -offset);
}

function observedDate(year: number, month: number, day: number): Date {
  const date = new Date(Date.UTC(year, month, day));
  if (date.getUTCDay() === 6) return addUtcDays(date, -1);
  if (date.getUTCDay() === 0) return addUtcDays(date, 1);
  return date;
}

// Meeus/Jones/Butcher Gregorian Easter algorithm.
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

function holidaysForYear(year: number): Map<string, string> {
  const holidays = new Map<string, string>();
  const add = (date: Date, name: string) => holidays.set(dateKeyFromUtcDate(date), name);

  add(observedDate(year, 0, 1), "New Year's Day");
  add(nthWeekdayOfMonth(year, 0, 1, 3), 'Martin Luther King Jr. Day');
  add(nthWeekdayOfMonth(year, 1, 1, 3), "Presidents' Day");
  add(addUtcDays(easterSunday(year), -2), 'Good Friday');
  add(lastWeekdayOfMonth(year, 4, 1), 'Memorial Day');
  if (year >= 2022) add(observedDate(year, 5, 19), 'Juneteenth');
  add(observedDate(year, 6, 4), 'Independence Day');
  add(nthWeekdayOfMonth(year, 8, 1, 1), 'Labor Day');
  add(nthWeekdayOfMonth(year, 10, 4, 4), 'Thanksgiving Day');
  add(observedDate(year, 11, 25), 'Christmas Day');
  return holidays;
}

function getHolidayName(dateKey: string): string | null {
  const date = utcDateFromKey(dateKey);
  if (!date) return null;
  const year = date.getUTCFullYear();
  for (const candidateYear of [year - 1, year, year + 1]) {
    const name = holidaysForYear(candidateYear).get(dateKey);
    if (name) return name;
  }
  return null;
}

export function getNewYorkDateKey(value: Date | number): string {
  const date = value instanceof Date ? value : new Date(value);
  return formatInTimeZone(date, NEW_YORK_TIME_ZONE, 'yyyy-MM-dd');
}

export function getNewYorkMinutes(value: Date | number): number {
  const date = value instanceof Date ? value : new Date(value);
  return (Number(formatInTimeZone(date, NEW_YORK_TIME_ZONE, 'H')) * 60)
    + Number(formatInTimeZone(date, NEW_YORK_TIME_ZONE, 'm'));
}

export function buildNewYorkTimestamp(dateKey: string, hour: number, minute: number): number {
  return fromZonedTime(`${dateKey}T${pad(hour)}:${pad(minute)}:00`, NEW_YORK_TIME_ZONE).getTime();
}

export function getUsEquityMarketDateStatus(
  dateKey: string,
  now: Date = new Date(),
): UsEquityMarketDateStatus {
  const date = utcDateFromKey(dateKey);
  const currentDateKey = getNewYorkDateKey(now);
  if (!date) {
    return {
      dateKey,
      valid: false,
      reason: 'The trade date is invalid.',
      holidayName: null,
      isFuture: false,
    };
  }

  const isFuture = dateKey > currentDateKey;
  if (isFuture) {
    return {
      dateKey,
      valid: false,
      reason: `${dateKey} is in the future in the U.S. equity market timezone.`,
      holidayName: null,
      isFuture: true,
    };
  }

  const weekday = date.getUTCDay();
  if (weekday === 0 || weekday === 6) {
    return {
      dateKey,
      valid: false,
      reason: `${dateKey} is a weekend, when U.S. equity markets are closed.`,
      holidayName: null,
      isFuture: false,
    };
  }

  const holidayName = getHolidayName(dateKey);
  if (holidayName) {
    return {
      dateKey,
      valid: false,
      reason: `${dateKey} is ${holidayName}, when U.S. equity markets are closed.`,
      holidayName,
      isFuture: false,
    };
  }

  return { dateKey, valid: true, reason: null, holidayName: null, isFuture: false };
}

export function getRecentUsEquitySessionDateKeys(
  referenceDate: Date,
  count: number,
  options?: { latestRequiredMinute?: number },
): string[] {
  const referenceKey = getNewYorkDateKey(referenceDate);
  const referenceUtcDate = utcDateFromKey(referenceKey);
  if (!referenceUtcDate || count <= 0) return [];

  const latestRequiredMinute = Number(options?.latestRequiredMinute ?? 0);
  const referenceMinutes = getNewYorkMinutes(referenceDate);
  let cursor = referenceUtcDate;
  if (Number.isFinite(latestRequiredMinute) && referenceMinutes < latestRequiredMinute) {
    cursor = addUtcDays(cursor, -1);
  }

  const result: string[] = [];
  while (result.length < count) {
    const dateKey = dateKeyFromUtcDate(cursor);
    if (getUsEquityMarketDateStatus(dateKey, referenceDate).valid) result.push(dateKey);
    cursor = addUtcDays(cursor, -1);
  }
  return result;
}
